import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';

// ─────────────────────────────────────────────────────────────────────────────
// Bench Clips — review draft "action" clips from the repair bench cameras
// (db_schema/bench_clips.sql, 56_Bench_Clips/56_bench_clips.py)
//
// [56] picks moments from the bench transcript each night and exports them in
// 4K from both cameras onto the Z4. This page plays the 720p previews from the
// private 'bench-clips' bucket (signed URLs) and records Keep / Discard. The
// files only move or get deleted on the NEXT nightly run, so a decision can be
// changed until then (files_removed_at still empty).
//
// Unreviewed drafts are removed after DRAFT_DAYS — same constant as the script.
// ─────────────────────────────────────────────────────────────────────────────

const DRAFT_DAYS = 21;
const INITIALS_KEY = 'benchClipsInitials';

const STATUSES = [
  { key: 'draft',          label: 'To Review' },
  { key: 'kept',           label: 'Kept' },
  { key: 'discarded',      label: 'Discarded' },
  { key: 'expired',        label: 'Expired' },
  { key: 'pending_export', label: 'Exporting' },
  { key: 'failed',         label: 'Failed' },
  { key: 'all',            label: 'All' },
];

const STATUS_COLOR = {
  draft:          'var(--gold)',
  kept:           '#16a34a',
  discarded:      '#dc2626',
  expired:        'var(--muted)',
  pending_export: '#2563eb',
  failed:         '#dc2626',
};

const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-NZ', {
  timeZone: 'Pacific/Auckland', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
});
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-NZ', {
  weekday: 'short', day: 'numeric', month: 'short',
});
const durationLabel = (c) => {
  const s = Math.round((new Date(c.end_at) - new Date(c.start_at)) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const daysLeft = (c) => {
  const age = (Date.now() - new Date(c.created_at)) / 86400000;
  return Math.max(0, Math.ceil(DRAFT_DAYS - age));
};

const Badge = ({ color, children, title }) => (
  <span title={title} style={{
    border: `1px solid ${color}`, color, borderRadius: 10,
    padding: '1px 8px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
);

// ── One clip ─────────────────────────────────────────────────────────────────
const ClipCard = ({ clip, initials, onDecide, saving }) => {
  const cams = Object.keys(clip.previews || {});
  const [cam, setCam]   = useState(cams.includes('Top Camera') ? 'Top Camera' : cams[0]);
  const [url, setUrl]   = useState(null);
  const [note, setNote] = useState(clip.review_note || '');
  const filesGone = !!clip.files_removed_at;
  const canDecide = !filesGone && ['draft', 'kept', 'discarded'].includes(clip.status);

  useEffect(() => {
    let live = true;
    setUrl(null);
    const path = clip.previews?.[cam];
    if (!path || filesGone) return undefined;
    supabase.storage.from('bench-clips').createSignedUrl(path, 3600).then(({ data }) => {
      if (live) setUrl(data?.signedUrl || null);
    });
    return () => { live = false; };
  }, [clip.previews, cam, filesGone]);

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'minmax(320px, 560px) 1fr', gap: 16,
      padding: 16, borderBottom: '1px solid var(--border)',
    }}>
      <div>
        {url ? (
          <video key={url} src={url} controls preload="metadata"
                 style={{ width: '100%', borderRadius: 6, background: '#000', aspectRatio: '16 / 9' }} />
        ) : (
          <div style={{
            width: '100%', aspectRatio: '16 / 9', borderRadius: 6, background: 'var(--bg3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--muted)', fontSize: 12,
          }}>
            {filesGone ? 'Files removed' : clip.status === 'pending_export' ? 'Export pending (next nightly run)'
              : clip.status === 'failed' ? 'No footage' : cams.length ? 'Loading…' : 'No preview'}
          </div>
        )}
        {cams.length > 1 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            {cams.map(c => (
              <button key={c}
                className={`action-btn ${cam === c ? 'action-btn--active' : 'action-btn--ghost'}`}
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={() => setCam(c)}>
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{clip.title}</span>
          <Badge color={STATUS_COLOR[clip.status] || 'var(--muted)'}>
            {STATUSES.find(s => s.key === clip.status)?.label || clip.status}
          </Badge>
          {clip.likely_customer && (
            <Badge color="#d97706" title={clip.customer_note || ''}>⚠ likely customer</Badge>
          )}
          {clip.kind && <Badge color="var(--muted)">{clip.kind}</Badge>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
          {fmtDay(clip.recorded_on)} · {fmtTime(clip.start_at)}–{fmtTime(clip.end_at)} ({durationLabel(clip)})
          {clip.score != null && <> · score {Number(clip.score).toFixed(2)}</>}
          {clip.size_bytes ? <> · 4K {(clip.size_bytes / 1e9).toFixed(2)} GB</> : null}
          {clip.status === 'draft' && <> · removed in {daysLeft(clip)} day(s) if not reviewed</>}
        </div>
        {clip.summary && <p style={{ fontSize: 13, lineHeight: 1.5, margin: '10px 0 4px' }}>{clip.summary}</p>}
        {clip.why && (
          <p style={{ fontSize: 12, lineHeight: 1.5, margin: '4px 0', color: 'var(--muted)' }}>
            <strong>Why:</strong> {clip.why}
          </p>
        )}
        {clip.likely_customer && clip.customer_note && (
          <p style={{ fontSize: 12, margin: '4px 0', color: '#b45309' }}>
            <strong>Customer:</strong> {clip.customer_note}
          </p>
        )}
        {clip.error && <p style={{ fontSize: 12, margin: '4px 0', color: '#dc2626' }}>{clip.error}</p>}
        {clip.files && !filesGone && (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, fontFamily: 'monospace',
                        wordBreak: 'break-all' }}>
            {Object.entries(clip.files).map(([c, p]) => <div key={c}>{c}: {p}</div>)}
            {clip.status === 'kept' && (clip.files['Top Camera'] || '').includes('\\drafts\\') && (
              <div style={{ fontFamily: 'var(--font-body)' }}>Moves to the kept folder on the next nightly run.</div>
            )}
          </div>
        )}

        {canDecide && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)"
                   style={{ flex: '1 1 200px', fontSize: 12, padding: '5px 8px',
                            border: '1px solid var(--border)', borderRadius: 4 }} />
            <button className="action-btn action-btn--reset"
                    disabled={saving || !initials || clip.status === 'kept'}
                    title={!initials ? 'Choose who you are first' : undefined}
                    onClick={() => onDecide(clip, 'kept', note)}>
              ✓ Keep
            </button>
            <button className="action-btn action-btn--close"
                    disabled={saving || !initials || clip.status === 'discarded'}
                    title={!initials ? 'Choose who you are first' : undefined}
                    onClick={() => onDecide(clip, 'discarded', note)}>
              ✕ Discard
            </button>
            {clip.status !== 'draft' && (
              <button className="action-btn action-btn--ghost" disabled={saving}
                      onClick={() => onDecide(clip, 'draft', note)}>
                Undo
              </button>
            )}
          </div>
        )}
        {clip.reviewed_by && (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
            {clip.status === 'kept' ? 'Kept' : clip.status === 'discarded' ? 'Discarded' : 'Reviewed'} by{' '}
            {clip.reviewed_by} {clip.reviewed_at && new Date(clip.reviewed_at).toLocaleString('en-NZ')}
            {clip.review_note && <> — {clip.review_note}</>}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const BenchClips = () => {
  const [clips, setClips]     = useState([]);
  const [staff, setStaff]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [statusFilter, setStatusFilter] = useState('draft');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [initials, setInitialsState] = useState(
    () => window.localStorage.getItem(INITIALS_KEY) || ''
  );
  const setInitials = (v) => {
    setInitialsState(v);
    if (v) window.localStorage.setItem(INITIALS_KEY, v);
    else   window.localStorage.removeItem(INITIALS_KEY);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [c, s] = await Promise.all([
      supabase.from('bench_clips').select('*')
        .order('recorded_on', { ascending: false }).order('start_at'),
      supabase.from('staff').select('id, name, initials').order('initials'),
    ]);
    if (c.error || s.error) setError((c.error || s.error).message);
    setClips(c.data || []);
    setStaff(s.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (clip, status, note) => {
    setSaving(true);
    const patch = status === 'draft'
      ? { status, reviewed_by: null, reviewed_at: null, review_note: note || null }
      : { status, reviewed_by: initials, reviewed_at: new Date().toISOString(), review_note: note || null };
    // Only while the nightly run hasn't acted on it yet.
    const { error: upErr } = await supabase.from('bench_clips').update(patch)
      .eq('id', clip.id).is('files_removed_at', null);
    if (upErr) setError(`Save failed: ${upErr.message}`);
    else setClips(list => list.map(c => (c.id === clip.id ? { ...c, ...patch } : c)));
    setSaving(false);
  };

  const counts = useMemo(() => {
    const c = { all: clips.length };
    clips.forEach(x => { c[x.status] = (c[x.status] || 0) + 1; });
    return c;
  }, [clips]);

  const filtered = useMemo(() => clips.filter(c =>
    (statusFilter === 'all' || c.status === statusFilter) &&
    (customerFilter === 'all' || (customerFilter === 'customer') === c.likely_customer)
  ), [clips, statusFilter, customerFilter]);

  const byDay = useMemo(() => {
    const m = new Map();
    filtered.forEach(c => { if (!m.has(c.recorded_on)) m.set(c.recorded_on, []); m.get(c.recorded_on).push(c); });
    return [...m.entries()];
  }, [filtered]);

  return (
    <main className="app-main">
      <div style={{ padding: '24px 32px' }}>
        <div className="dash-panel">
          <div className="dash-panel__header">
            <span className="dash-panel__title">
              🎬 Bench Clips
              {counts.draft > 0 && (
                <span style={{
                  marginLeft: 8, background: 'var(--gold)', color: '#000',
                  borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700,
                }}>
                  {counts.draft} to review
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={initials} onChange={e => setInitials(e.target.value)}
                      style={{ fontSize: 11, padding: '3px 6px' }}>
                <option value="">Reviewing as…</option>
                {staff.map(s => <option key={s.id} value={s.initials}>{s.initials} — {s.name}</option>)}
              </select>
              <button className="action-btn action-btn--ghost" style={{ fontSize: 10, padding: '2px 10px' }}
                      onClick={load} disabled={loading}>
                ↻ Refresh
              </button>
            </div>
          </div>

          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)',
                        fontSize: 11, color: 'var(--muted)', lineHeight: 1.6 }}>
            Moments from the repair bench picked each night from what the bench mic heard, exported in 4K from
            both cameras onto the Z4 (preview here is 720p). <strong>Keep</strong> moves the 4K files to the
            kept folder; <strong>Discard</strong> deletes them — both happen on the next nightly run, so you can
            change your mind until then. Clips not reviewed within {DRAFT_DAYS} days are removed.
            <strong> ⚠ likely customer</strong> means a customer was probably in the conversation — check
            who's in shot before using it.
          </div>

          {error && (
            <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 11, color: '#dc2626' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
                        padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
            {STATUSES.map(s => (
              <button key={s.key}
                className={`action-btn ${statusFilter === s.key ? 'action-btn--active' : 'action-btn--ghost'}`}
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={() => setStatusFilter(s.key)}>
                {s.label} ({counts[s.key] || 0})
              </button>
            ))}
            <select value={customerFilter} onChange={e => setCustomerFilter(e.target.value)}
                    style={{ fontSize: 11, padding: '3px 6px' }}>
              <option value="all">Customer: any</option>
              <option value="customer">Likely customer</option>
              <option value="none">No customer</option>
            </select>
          </div>

          {loading ? (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--muted)' }}>Loading…</div>
          ) : byDay.length === 0 ? (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--muted)' }}>No clips here.</div>
          ) : byDay.map(([day, list]) => (
            <div key={day}>
              <div style={{ padding: '8px 16px', background: 'var(--bg3)', fontSize: 12, fontWeight: 700,
                            borderBottom: '1px solid var(--border)' }}>
                {fmtDay(day)} — {list.length} clip{list.length === 1 ? '' : 's'}
              </div>
              {list.map(c => (
                <ClipCard key={c.id} clip={c} initials={initials} onDecide={decide} saving={saving} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default BenchClips;
